import Button from "components/button";

import { ExternalLink } from "../common";
import type { FaqSection } from "./types";

const platformFeatures = [
  {
    title: "Sales Core",
    description:
      "Deal pipeline tracking, scheduling, AI-powered PV system design, battery sizing tool, title check, soft pulls, savings forecaster, proposal builder, finance integrations, real-time pricing, contract generation, and e-signatures.",
  },
  {
    title: "Install Tracker",
    description:
      "End-to-end solar project management software for installation workflows, milestones, and operational visibility.",
  },
  {
    title: "Customer Portal",
    description:
      "A white-labeled homeowner-facing portal to showcase past jobs and reviews, track project status, centralize documents and communications, and incentivize referrals.",
  },
  {
    title: "Virtual Sales Rep (VSR)",
    description: "Web-based self-serve quoting for homeowners, increasing conversion without requiring live reps.",
  },
  {
    title: "Robust Reporting & Analytics",
    description: (
      <>
        Data-driven insights with DOMO-powered dashboards to help <strong>solar businesses</strong> make smarter
        decisions.
      </>
    ),
  },
  {
    title: "API & CRM Integrations",
    description:
      "Native and custom integrations with industry-leading tools, plus optional integration monitoring services.",
  },
  {
    title: "Custom Pricing Engine",
    description: "Configurable savings forecasts, battery sizing, and finance options.",
  },
  {
    title: "Document Automation",
    description: "Automatic creation of perfect signing packets and streamlined document approval processes.",
  },
  {
    title: "Bilingual Support",
    description: "Spanish language homeowner experience to reach more customers.",
  },
];

const generalSection: FaqSection = {
  id: "general-platform",
  title: "General Platform FAQs",
  faqs: [
    {
      question: "What is Enerflo and how does it help residential solar companies?",
      answer: (
        <p>
          <Button href="/" variant="link">
            Enerflo
          </Button>{" "}
          is an all-in-one solar business optimization platform designed specifically for residential solar installers,
          sales dealers, hybrid solar companies, and vertically integrated providers. It connects Contractors’ existing
          solar software tools—covering sales, financing, operations, and installation—into one seamless workflow. By
          unifying the entire solar project lifecycle, Enerflo helps solar companies reduce soft costs, shorten project
          timelines, improve homeowner experiences, and scale profitably. Think of Enerflo as the source of truth or
          system of record for all solar projects at all phases of their lifecycle.
        </p>
      ),
    },
    {
      question: "What features are included in the Enerflo platform?",
      answer: (
        <>
          <p>Enerflo combines multiple core systems into a single, integrated platform for solar companies:</p>
          <br />
          <ul className="list-disc space-y-1 ml-4">
            {platformFeatures.map(item => (
              <li key={item.title}>
                <strong>{item.title}</strong> – {item.description}
              </li>
            ))}
          </ul>
        </>
      ),
    },
    {
      question: "What makes Enerflo different from other solar software platforms?",
      answer: (
        <>
          <p>
            Enerflo is non-competitive—we don’t sell leads or solar directly. Instead, we empower our partners with
            integrations to best-in-class solar tools like{" "}
            <ExternalLink href="https://aurorasolar.com/">Aurora Solar</ExternalLink>,{" "}
            <ExternalLink href="https://palmetto.com/products/lightreach">Palmetto LightReach</ExternalLink>,{" "}
            <ExternalLink href="https://www.salesforce.com/">Salesforce</ExternalLink>, and more. Unlike closed systems,
            Enerflo is CRM-agnostic and supports multi-organization flexibility, custom workflows, and scalable growth
            for solar businesses.
          </p>
          <br />
          <p>
            Our volume-based, flat-rate monthly subscription helps stabilize cash flow by avoiding per-design,
            per-proposal, per-seat, or per-contract fees—making budgeting predictable for solar companies of any size.
          </p>
        </>
      ),
    },
    {
      question: "Does Enerflo replace my CRM?",
      answer: (
        <p>
          It can—depending on your needs. Enerflo provides built-in CRM functionality but is designed to integrate
          seamlessly with third-party CRMs for teams with more complex requirements. Whether you use Salesforce,{" "}
          <ExternalLink href="https://www.hubspot.com/">HubSpot</ExternalLink>, or another CRM, Enerflo can centralize
          your solar operations and store all of your project data without forcing you to abandon existing systems.
        </p>
      ),
    },
    {
      question: "Does Enerflo replace my solar design tool?",
      answer: (
        <p>
          Yes, if you choose. Enerflo includes a deep integration with{" "}
          <ExternalLink href="https://aerialytic.ai/">Aerialytic</ExternalLink>, which is accepted by most solar
          financing providers and covers 99% of U.S. rooftops. If your team already uses Aurora Solar or{" "}
          <ExternalLink href="https://www.opensolar.com/">OpenSolar</ExternalLink>, Enerflo integrates with those tools
          as well—offering flexibility without retraining your staff.
        </p>
      ),
    },
    {
      question: "Does Enerflo replace my proposal tool?",
      answer: (
        <p>
          Yes. Enerflo’s native proposal software, Optimus, includes a savings calculator, battery sizing tool, finance
          integrations, custom content management, and more—delivering high-converting, accurate proposals instantly.
        </p>
      ),
    },
    {
      question: "Is Enerflo only for residential solar companies?",
      answer: (
        <p>
          Yes, at this time, Enerflo is purpose-built for the residential solar industry (and light commercial). The
          Enerflo Platform focuses on improving the homeowner journey, streamlining backend workflows, and reducing
          operational friction from first contact to post-install referral generation.
        </p>
      ),
    },
  ],
};

export default generalSection;
